import { useDecision } from '@optimizely/react-sdk'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { Fragment } from 'react'

import { ErrorBoundary } from '@/components/shared/error-boundary'
import { ListingCard } from '@/components/srp/listing-card'
import { AutosCrossPromoListingCard } from '@/components/srp/listing-card/autos-x-promo-listing-card/AutosCrossPromoListingCard'
import { AutosListingCard } from '@/components/srp/listing-card/AutosListingCard'
import { type ListingCardPrice } from '@/components/srp/listing-card/ListingCard'
import { ShoppingAds } from '@/components/srp/shopping-ads'
import { getListingProximityLabel } from '@/domain/srp/getListingProximityLabel'
import { isSeeAllTopAdsView } from '@/domain/srp/isSeeAllTopAdsView'
import { SearchListIntersectionObserver } from '@/domain/srp/SearchListIntersectionObserver'
import { trackEventListingClick } from '@/domain/srp/tracking/trackEventListingClick'
import { seoUrlToString } from '@/domain/urls'
import { type ListingFragment } from '@/generated'
import { useLocale } from '@/hooks/useLocale'
import { InlineAdSlot } from '@/lib/ads/components/ad-slots'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { type SearchPageType } from '@/lib/ga/events/search'
import { FEATURE_FLAG } from '@/lib/optimizely'
import { type Listing, isAutosListingPrice } from '@/types/search'
import { sendToLogger } from '@/utils/sendToLogger'

import {
  ATTRIBUTES,
  FLAGS,
  getAdSourceForPromotionListing,
  getAttributesDictionary,
  getCarsVehiclesAttributes,
  getFlagsDictionary,
  getListingBadgeName,
  getTransformedPriceType,
  getTransmissionTypeLiteralKey,
  shouldShowPriceDrop,
} from './domain'
import { getRealEstateListingAttributes } from './getRealEstateListingAttributes'
import { RenderZipRecruiterListing } from './RenderZipRecruiterListing'

type RenderListingCardProps = {
  casBackfillsStartIndex?: number
  globalKeyword?: string
  index: number
  isTopAd?: boolean
  item: Listing
  listings: ListingFragment[]
  showCasBackfills?: boolean
  isCasBackFillsToggleEnabled?: boolean
  isLowSrp?: boolean
  showCasBackfillsPanel?: boolean
  isCarsTrucksSearch: boolean
  isAlbertaSearch: boolean
  isCrossPromotionCard?: boolean
  searchPageType: SearchPageType
  isMobile?: boolean
}

const ValueEstimate = dynamic(
  () => import('@/components/srp/value-estimate').then((mod) => mod.ValueEstimate),
  { ssr: false }
)

export const RenderListingCard = ({
  globalKeyword,
  index,
  isTopAd,
  item,
  listings,
  casBackfillsStartIndex = -1,
  isCasBackFillsToggleEnabled = false,
  showCasBackfillsPanel = false,
  isCrossPromotionCard,
  isCarsTrucksSearch,
  isAlbertaSearch,
  searchPageType,
  isMobile,
}: RenderListingCardProps) => {
  /**
   * It should check the current category based on the listing data not activeCategory cache
   * If the active category is ALL CATEGORIES, none of the listing specific logic would work
   */
  const listingCategoryId = item.categoryId

  const { t } = useTranslation('srp')
  const { routeLocale } = useLocale()
  const { query } = useRouter()
  const [valueEstimateFlag, isClientReady] = useDecision(FEATURE_FLAG.SRP_ICO)

  const flagsDictionary = getFlagsDictionary(item.flags)
  const attributesDictionary = getAttributesDictionary(item.attributes)

  const isSeeAllTopAd = isSeeAllTopAdsView(query)

  const showcaseImages = flagsDictionary[FLAGS.SHOWCASE] ? item.imageUrls?.slice(1) : []

  const hasShowcaseImages = showcaseImages.length > 0
  const hasFinancingOption = flagsDictionary[FLAGS.FINANCING]

  const handleTrackListingClick = (photoshowcaseClick?: boolean) => {
    trackEventListingClick({
      listing: item,
      position: index,
      isSeeAllTopAd,
      keywords: globalKeyword,
      photoshowcaseClick,
    })
  }

  const handleTrackAutosCrossPromoListingClick = () => {
    trackEvent({
      action: GA_EVENT.CrossPromoBadgeClick,
      label: `badge=Kijiji Autos`,
    })
  }

  /**
   * For low inventory SRPs (<= 20 organic listings) the BE may return CAS backfills at the end of the listings
   * We want to show the first and hide the rest unless the user clicks on the show more button
   * This feature is currently under an optimizely flag
   */
  if (isCasBackFillsToggleEnabled && showCasBackfillsPanel) {
    if (!isTopAd && index > casBackfillsStartIndex) {
      return null
    }
  }

  /** If is a conventional listing, then find all the values needed to render it */
  const hasPriceDropValue = shouldShowPriceDrop(
    flagsDictionary[FLAGS.PRICE_DROP],
    item.price.originalPrice?.amount
  )
  const priceType = getTransformedPriceType(item.price.type)
  const surcharges = isAutosListingPrice(item.price) ? item.price.surcharges : undefined
  const imageBadge = getListingBadgeName(listingCategoryId, flagsDictionary[FLAGS.CATEGORY_BADGE])
  const price: ListingCardPrice = {
    amount: item.price?.amount,
    original: hasPriceDropValue ? item.price.originalPrice?.amount : undefined,
    type: priceType,
    surcharges,
  }
  /**
   * Distance
   * Should return the proximity value from the user in meters
   */
  const proximity = getListingProximityLabel(item.location?.distance)

  /**
   * Get attributes for specific categories
   * To save processing, the listing will only search for certain attributes if is the right category
   * */
  const { additionalOptions, realEstateAttributes, rentalBadge, virtualTour } =
    getRealEstateListingAttributes(
      attributesDictionary,
      item.location.nearestIntersection,
      listingCategoryId,
      t
    )

  const { carfax, carsVehiclesAttributes, cpo, dealerUpdates, isNew } = getCarsVehiclesAttributes(
    attributesDictionary,
    listingCategoryId,
    routeLocale,
    t
  )

  const key = `listing-${item.id}-${index}`

  const indexIsSix = index === 6

  const promotionLabelValue = getAdSourceForPromotionListing(
    item.flags,
    item.adSource,
    isSeeAllTopAd
  )

  const getListingCardComponent = () => {
    const listingLocation = {
      id: item.location.id,
      name: item.location.name,
      address: item.location.address,
    }

    if (isCrossPromotionCard) {
      if (!item.autosVipUrl) {
        sendToLogger('Tried to render cross promotion card without KA Vip url.', {
          fingerprint: ['RenderListingCard'],
        })
        return null
      }

      return (
        <AutosCrossPromoListingCard
          handleTrackListingClick={handleTrackAutosCrossPromoListingClick}
          id={item.id}
          listingLocation={listingLocation}
          title={item.title}
          // Even though our GraphQL types say that autosVipUrl is an URL, is not correct. It is a string.
          // This is because we don't have any resolver for the URL scalar in NWA yet. Pending to be added in KCAN-23304.
          url={seoUrlToString(item.autosVipUrl)}
          attributes={attributesDictionary}
          mainImage={item.imageUrls?.[0]}
          price={price}
          msrp={isNew ? item.price.msrp?.amount : undefined}
          searchPageType={searchPageType}
        />
      )
    }

    if (isCarsTrucksSearch) {
      return (
        <AutosListingCard
          adSource={item.adSource}
          categoryId={listingCategoryId}
          activationDate={item.activationDate}
          sortingDate={item.sortingDate}
          additionalOptions={additionalOptions}
          attributes={{
            dealerUpdates,
            hasCarfaxReport: carfax,
            isCertifiedPreOwned: cpo,
            msrp: isNew ? item.price.msrp?.amount : undefined,
            km: attributesDictionary[ATTRIBUTES.KILOMETERS]?.[0],
            transmission: attributesDictionary[ATTRIBUTES.TRANSMISSION]?.[0]
              ? t(
                  getTransmissionTypeLiteralKey(+attributesDictionary[ATTRIBUTES.TRANSMISSION]?.[0])
                )
              : undefined,
          }}
          imageBadge={imageBadge}
          description={item.description}
          id={item.id}
          isHighlight={flagsDictionary[FLAGS.HIGHLIGHT]}
          listingLocation={listingLocation}
          logoSrc={attributesDictionary[ATTRIBUTES.LOGO]?.[0]}
          mainImage={item.imageUrls?.[0]}
          price={price}
          proximity={proximity}
          showcaseImages={flagsDictionary[FLAGS.SHOWCASE] ? item.imageUrls?.slice(1) : []}
          title={item.title}
          url={item.seoUrl}
          handleTrackListingClick={handleTrackListingClick}
          promotionLabel={promotionLabelValue}
          isInTopAdsSection={isTopAd || isSeeAllTopAd}
          isMobile={isMobile}
        />
      )
    }

    return (
      <ListingCard
        adSource={item.adSource}
        categoryId={listingCategoryId}
        activationDate={item.activationDate}
        sortingDate={item.sortingDate}
        additionalOptions={additionalOptions}
        autos={{
          dealerUpdates,
          hasCarfaxReport: carfax,
          isCertifiedPreOwned: cpo,
          msrp: isNew ? item.price.msrp?.amount : undefined,
          hasFinancingOption,
        }}
        imageBadge={imageBadge}
        description={item.description}
        id={item.id}
        inlineAttributes={carsVehiclesAttributes}
        realEstateAttributes={realEstateAttributes}
        isHighlight={flagsDictionary[FLAGS.HIGHLIGHT]}
        listingLocation={listingLocation}
        logoSrc={attributesDictionary[ATTRIBUTES.LOGO]?.[0]}
        mainImage={item.imageUrls?.[0]}
        price={price}
        proximity={proximity}
        realEstate={{ rentalBadge, virtualTour }}
        isShippedBySeller={flagsDictionary[FLAGS.SHIPPING]}
        showcaseImages={showcaseImages}
        title={item.title}
        url={item.seoUrl}
        handleTrackListingClick={() => handleTrackListingClick(hasShowcaseImages)}
        promotionLabel={promotionLabelValue}
        isInTopAdsSection={isTopAd || isSeeAllTopAd}
        isMobile={isMobile}
      />
    )
  }

  /** It is a zipRecruiter listing if its id is prefixed with "zip_" */
  const isZipRecruiterListing =
    item.id.startsWith('zip_') && flagsDictionary[FLAGS.EXTERNAL_LISTING]
  /** If is a zip-recruiter listing, then trigger an early return to avoid processing any information that is not needed */
  if (isZipRecruiterListing) {
    return (
      <RenderZipRecruiterListing
        handleTrackListingClick={handleTrackListingClick}
        index={index}
        item={item}
        listings={listings}
      />
    )
  }

  return (
    <Fragment key={key}>
      <SearchListIntersectionObserver data-testid={`listing-card-list-item-${index}`} index={index}>
        <ErrorBoundary fingerprintId="RenderListingCard">{getListingCardComponent()}</ErrorBoundary>
      </SearchListIntersectionObserver>

      {indexIsSix && <ShoppingAds />}
      {!isTopAd && (
        <>
          <InlineAdSlot index={index} totalListings={listings.length} />
          {isClientReady && valueEstimateFlag.enabled && isCarsTrucksSearch && isAlbertaSearch && (
            <ValueEstimate index={index} />
          )}
        </>
      )}
    </Fragment>
  )
}
